export type SettingsTab = "customize" | "general";

export interface SettingsTabsElements {
  readonly backButton: HTMLButtonElement;
  readonly customizePanel: HTMLElement;
  readonly customizeTab: HTMLButtonElement;
  readonly generalPanel: HTMLElement;
  readonly generalTab: HTMLButtonElement;
  readonly settingsButton: HTMLButtonElement;
}

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing settings tabs element: ${id}`);
  }
  return element;
};

export class SettingsTabs {
  private activeTab: SettingsTab = "customize";

  public constructor(
    private readonly elements: SettingsTabsElements,
    private readonly renderCustomize: () => void = () => undefined,
    private readonly renderGeneral: () => void = () => undefined,
    private readonly closeGeneral: () => void = () => undefined,
  ) {
    elements.settingsButton.addEventListener("click", this.handleOpen);
    elements.backButton.addEventListener("click", this.handleBack);
    elements.customizeTab.addEventListener("click", this.handleCustomize);
    elements.generalTab.addEventListener("click", this.handleGeneral);
    this.render();
  }

  public destroy(): void {
    this.elements.settingsButton.removeEventListener("click", this.handleOpen);
    this.elements.backButton.removeEventListener("click", this.handleBack);
    this.elements.customizeTab.removeEventListener(
      "click",
      this.handleCustomize,
    );
    this.elements.generalTab.removeEventListener("click", this.handleGeneral);
  }

  private readonly handleBack = (): void => {
    this.closeGeneral();
    this.elements.settingsButton.focus();
  };

  private readonly handleCustomize = (): void => {
    this.selectTab("customize");
  };

  private readonly handleGeneral = (): void => {
    this.selectTab("general");
  };

  private readonly handleOpen = (): void => {
    this.selectTab("customize");
    this.elements.customizeTab.focus();
  };

  private render(): void {
    const customizeActive = this.activeTab === "customize";
    this.elements.customizePanel.hidden = !customizeActive;
    this.elements.generalPanel.hidden = customizeActive;
    this.renderTab(this.elements.customizeTab, customizeActive);
    this.renderTab(this.elements.generalTab, !customizeActive);
    if (customizeActive) {
      this.closeGeneral();
      this.renderCustomize();
    } else {
      this.renderGeneral();
    }
  }

  private renderTab(button: HTMLButtonElement, selected: boolean): void {
    button.setAttribute("aria-selected", String(selected));
    button.tabIndex = 0;
  }

  private selectTab(tab: SettingsTab): void {
    this.activeTab = tab;
    this.render();
  }
}

export const installSettingsTabs = (
  documentScope: Document,
  renderCustomize?: () => void,
  renderGeneral?: () => void,
  closeGeneral?: () => void,
): SettingsTabs =>
  new SettingsTabs(
    {
      backButton: requireElement(
        documentScope,
        "settings-back-button",
      ) as HTMLButtonElement,
      customizePanel: requireElement(documentScope, "settings-customize-panel"),
      customizeTab: requireElement(
        documentScope,
        "settings-customize-tab",
      ) as HTMLButtonElement,
      generalPanel: requireElement(documentScope, "settings-general-panel"),
      generalTab: requireElement(
        documentScope,
        "settings-general-tab",
      ) as HTMLButtonElement,
      settingsButton: requireElement(
        documentScope,
        "settings-button",
      ) as HTMLButtonElement,
    },
    renderCustomize,
    renderGeneral,
    closeGeneral,
  );
